import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import { type GrantRowModel, buildGrantRow, kindOfResource } from "permissions/_views/grant-row/grant-row-model";
import { standingKinds } from "permissions/_views/people/person-grants-model";
import { resourceForKind, withLevel } from "permissions/_views/grant-row/use-grant-changes";
import type { GrantLevel } from "permissions/_views/wording/permission-wording";
import Permission from "permissions/permission";
import type { Resource } from "permissions/resources/permission-resource";
import { resourceFromJSON } from "permissions/resources/permission-resource-factory";
import type { AnyResourceJSON } from "permissions/resources/permission-resource-types";

/**
 * A template drawn with grant rows (`docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "Templates").
 *
 * A template names some kinds and says, for each, either "grant it at this level" or "take it away".
 * Applying it replaces a person's grants of each kind it names, and only of those kinds: the server
 * keys the replacement by kind, not by resource type (`PermissionManagementService.AddPermissionsAsync`),
 * so a template naming member information leaves signing authority — another kind of the same
 * `LegalEntityAccess` type — alone. Named or not is therefore the difference the editor makes plain:
 *
 * - **Granted**: the row's switch is on and its level is the template's.
 * - **Taken away**: the switch is off; applying the template removes the kind from whoever holds it.
 * - **Not in the template**: a quiet Grant, under "Not in the template (n)"; applying it changes
 *   nothing of that kind.
 */

export type TemplateKindRow = Readonly<{
  /**
   * The kind's slug, and for a named kind which of its resources this is. Not the resource's position
   * in the template: leaving one kind out would move every later row's key, and an open menu or level
   * picker with it.
   */
  key: string;
  kind: PermissionKind;
  /** The template's resource for the kind, or none when the template does not name it. */
  resource?: Resource;
  /** Where `resource` is in the template's resources, for the edits that act on it. */
  index?: number;
}>;

export type TemplateAreaSection = Readonly<{
  area: string;
  included: readonly TemplateKindRow[];
  notIncluded: readonly TemplateKindRow[];
  grantedCount: number;
  takenAwayCount: number;
}>;

/** Every template row is drawn with the same stand-in ids: it is a template's row, not anybody's grant. */
const TEMPLATE_ROW_GRANTEE = new Guid("00000000-0000-4000-8000-000000000001");

function enabled(resource: Resource): Resource {
  const json = { ...(resource.toJSON() as Record<string, unknown>) };
  delete json.isDisabled;
  return resourceFromJSON(json as AnyResourceJSON);
}

/**
 * The template's rows, area by area in the server's order. A kind the template names twice — an old
 * template can — draws one row per resource. Per-record kinds are not offered: a template is applied
 * to people, not to records, and none of the template editors ever offered them.
 */
export function buildTemplateAreas(
  areas: readonly string[],
  kinds: readonly PermissionKind[],
  resources: readonly Resource[],
  mayAdministerPlatform: boolean,
): TemplateAreaSection[] {
  const standing = standingKinds(kinds).filter(
    (kind) => !kind.retired && (!kind.superUserOnly || mayAdministerPlatform),
  );
  return areas
    .map((area) => {
      const included: TemplateKindRow[] = [];
      const notIncluded: TemplateKindRow[] = [];
      for (const kind of standing.filter((candidate) => candidate.area === area)) {
        const named = resources
          .map((resource, index) => ({ resource, index }))
          .filter(({ resource }) => kindOfResource(kinds, resource) === kind);
        if (named.length === 0) {
          if (kind.grantable) notIncluded.push({ key: `kind-${kind.slug}`, kind });
          continue;
        }
        included.push(
          ...named.map(({ resource, index }, occurrence) => ({ key: `${kind.slug}-${occurrence}`, kind, resource, index })),
        );
      }
      return {
        area,
        included,
        notIncluded,
        grantedCount: included.filter((row) => !row.resource?.isDisabled).length,
        takenAwayCount: included.filter((row) => row.resource?.isDisabled).length,
      };
    })
    .filter((section) => section.included.length > 0 || section.notIncluded.length > 0);
}

/**
 * The positions of the template's resources the editor draws no row for: a retired kind, a
 * per-record one, one only a platform administrator may grant (to anybody else), or a resource the
 * kinds table does not know. They are still saved and still applied, so the page says how many there
 * are and offers to leave them out.
 */
export function hiddenResourceIndexes(
  kinds: readonly PermissionKind[],
  resources: readonly Resource[],
  mayAdministerPlatform: boolean,
): number[] {
  return resources.flatMap((resource, index) => {
    const kind = kindOfResource(kinds, resource);
    const shown = kind && !kind.perRecord && !kind.retired && (!kind.superUserOnly || mayAdministerPlatform);
    return shown ? [] : [index];
  });
}

/** The template without the resources at `indexes`. */
export function withoutResources(resources: readonly Resource[], indexes: readonly number[]): Resource[] {
  const leaving = new Set(indexes);
  return resources.filter((_, at) => !leaving.has(at));
}

/**
 * A template row as the grant row draws it. The row is built from a stand-in grant so its level,
 * controls and locks are the grant row's own rules; only the state words are the template's.
 */
export function templateRowModel(
  row: TemplateKindRow,
  kinds: readonly PermissionKind[],
  viewerUserId: Guid,
  mayAdministerPlatform: boolean,
): GrantRowModel {
  if (!row.resource) {
    const model = buildGrantRow({ kind: row.kind, kinds, viewerUserId, mayAdministerPlatform });
    // "Widen to organization" is what a person's notifications row offers; a template has no
    // baseline to widen, so it adds the kind like any other.
    return { ...model, lock: undefined, grantVerb: row.kind.grantable ? "grant" : undefined };
  }
  const takenAway = Boolean(row.resource.isDisabled);
  const model = buildGrantRow({
    kind: row.kind,
    kinds,
    permission: new Permission({
      resource: enabled(row.resource),
      granteeUserId: TEMPLATE_ROW_GRANTEE,
      grantorUserId: TEMPLATE_ROW_GRANTEE,
      entityId: TEMPLATE_ROW_GRANTEE,
      disabledDate: takenAway ? AHDate.TODAY : undefined,
      version: 0,
    }),
    viewerUserId,
    mayAdministerPlatform,
  });
  return {
    ...model,
    rowId: `template-${row.key}`,
    source: undefined,
    removedText: takenAway ? TAKEN_AWAY : undefined,
    // A template holds no grant of its own, so nothing about it is anybody's own or inherent.
    lock: model.lock?.reason === "global" ? model.lock : undefined,
    hasSwitch: true,
  };
}

/** What a switched-off template row does to whoever the template is applied to. */
export const TAKEN_AWAY = "Taken away when applied";

/** The template's resources with one kind added, granted at its default or the level given. */
export function withKindGranted(resources: readonly Resource[], kind: PermissionKind, level?: GrantLevel): Resource[] {
  return [...resources, resourceForKind(kind, level)];
}

/** The resource at `index` switched on (granted) or off (taken away). */
export function withResourceSwitched(resources: readonly Resource[], index: number, granted: boolean): Resource[] {
  return resources.map((resource, at) => {
    if (at !== index) return resource;
    const json = { ...(resource.toJSON() as Record<string, unknown>), isDisabled: !granted };
    return resourceFromJSON(json as AnyResourceJSON);
  });
}

/** The resource at `index` at another level, its switch as it was. */
export function withResourceLevel(resources: readonly Resource[], index: number, level: GrantLevel): Resource[] {
  return resources.map((resource, at) => {
    if (at !== index) return resource;
    const relevelled = withLevel(
      new Permission({ resource, granteeUserId: TEMPLATE_ROW_GRANTEE, grantorUserId: TEMPLATE_ROW_GRANTEE, entityId: TEMPLATE_ROW_GRANTEE }),
      level,
    ).resource;
    const json = { ...(relevelled.toJSON() as Record<string, unknown>), isDisabled: resource.isDisabled ?? false };
    return resourceFromJSON(json as AnyResourceJSON);
  });
}

/** The template without the resource at `index`: the kind is no longer named, and left alone. */
export function withoutResource(resources: readonly Resource[], index: number): Resource[] {
  return resources.filter((_, at) => at !== index);
}

export function resourceIndex(row: TemplateKindRow): number {
  return row.index ?? -1;
}
